/**
 * Content module — surface 2.3 "Template library" (industry × expertise).
 *
 * Every figure, name, id, route and label the page renders resolves HERE from
 * `@/demo/data`; the page component holds no literals (build brief §0a).
 *
 * Deck-given facts (product/px-platform-mockups.md §2.3), transcribed as
 * stated: the six vertical chips, the Odoo-version filter, the six table
 * columns, and the three panels (contribution 19 · retired 7 · effect −31%).
 * The seven packs themselves are deck-implied but unnamed — invented ONCE
 * here, owned by seeded people, and reused by anyone who needs them.
 *
 * ⚠ MIRRORED FACTS — `content/templateDetail.ts` (surface 2.4) renders the
 * hero pack's header from the same figures: `retail-rollout` is "Retail
 * rollout pack", Retail × Pricing & promotions, reused 9×, saves ~11 days,
 * owner Marc Dubois, V4 · Odoo 19. If one of those changes here it MUST change
 * there — the two surfaces are the same pack seen twice, and a mismatch is the
 * cheapest way to lose the room. Per-asset contents, the used-by outcomes and
 * the returned improvements belong to the detail surface and are deliberately
 * NOT restated on this one.
 */
import type { StatusTone } from '@/components/px/StatusPill'
import {
  GROUP_STATS,
  HERO_PROJECT_ID,
  PHASES,
  PORTFOLIO,
  PROJECTS,
  personById,
  projectById,
  type Person,
  type Project,
} from '@/demo/data'

/* ── chrome ─────────────────────────────────────────────────────────────── */

export const MODULE_LABEL = 'PX · Library'
export const BREADCRUMB = 'Library › Templates'
/** The lens this surface is: the library is a grid of industry × expertise. */
export const CONTEXT_PILL = 'INDUSTRY × EXPERTISE'

export const PAGE_TITLE = 'Template library'
export const PAGE_LEDE =
  'Every finished project deposits its blueprint, config, mappings and test sets back here, scored on how often they get reused.'

/* ── verticals + versions (the deck's own filter axes) ──────────────────── */

export const VERTICALS = [
  'Retail',
  'Manufacturing',
  'Food service',
  'Healthcare',
  'Construction',
  'Public sector',
] as const

export type Vertical = (typeof VERTICALS)[number]
export type VerticalFilter = 'all' | Vertical

/** Packs are published against one Odoo version — the group runs 19 and 18. */
export const ODOO_VERSIONS = [19, 18] as const
export type PackOdooVersion = (typeof ODOO_VERSIONS)[number]
export type OdooFilter = 'all' | PackOdooVersion

export const ALL = 'all'

/** `<select>` hands back strings; this is the only place that widens them. */
export const parseOdooFilter = (raw: string): OdooFilter =>
  ODOO_VERSIONS.find((version) => String(version) === raw) ?? ALL

export const odooOptionLabel = (value: OdooFilter): string =>
  value === ALL ? 'All versions' : `Odoo ${value}`

export const ODOO_OPTIONS: readonly OdooFilter[] = [ALL, ...ODOO_VERSIONS]

/* ── packs ──────────────────────────────────────────────────────────────── */

/**
 * A pack's status, per the deck's TemplatePack entity. `Published` is the
 * quiet default and shows no pill — weight is monotonic with exception, so a
 * pack that needs a human is the only one that raises its voice.
 */
export type PackStatus = 'Published' | 'Update in review'

export interface TemplatePack {
  id: string
  name: string
  /** The expertise half of the industry × expertise cell. */
  expertise: string
  /**
   * The verticals a pack has actually been PROVEN in — reuse is the evidence,
   * so a vertical-agnostic pack still only claims the cells it has run in.
   */
  verticals: readonly Vertical[]
  /** True when the pack is not built for one industry (migration, finance…). */
  crossVertical: boolean
  /** Kinds of asset inside; the per-asset list lives on the detail surface. */
  contains: readonly string[]
  reuseCount: number
  daysSaved: number
  ownerId: string
  version: string
  odooVersion: PackOdooVersion
  status: PackStatus
  /**
   * Projects this pack is known to have been spent on. Declared only where the
   * usage is sourced — the other packs' usage lists live on their own detail
   * pages, and claiming them here would be unsourced.
   */
  usedByProjectIds?: readonly string[]
}

/** The pack with a built detail view this round (route /library/templates/…). */
export const HERO_PACK_ID = 'retail-rollout'

/** Sorted by reuse — the score the library rule invests on. */
export const TEMPLATE_PACKS: readonly TemplatePack[] = [
  {
    id: HERO_PACK_ID,
    name: 'Retail rollout pack',
    expertise: 'Pricing & promotions',
    verticals: ['Retail'],
    crossVertical: false,
    contains: ['Blueprint', 'POS & pricing config', 'Migration mappings', 'Test sets', 'Training deck'],
    reuseCount: 9,
    daysSaved: 11,
    ownerId: 'marc-dubois',
    version: 'V4',
    odooVersion: 19,
    status: 'Published',
    usedByProjectIds: ['van-dijk', 'bergman'],
  },
  {
    id: 'migration-toolkit',
    name: 'Migration toolkit',
    expertise: 'Data migration',
    verticals: ['Retail', 'Food service', 'Manufacturing'],
    crossVertical: true,
    contains: ['Field maps', 'Load scripts', 'Validation rules', 'Rehearsal runbook'],
    reuseCount: 8,
    daysSaved: 6,
    ownerId: 'julien-roux',
    version: 'V5',
    odooVersion: 19,
    status: 'Published',
  },
  {
    id: 'manufacturing-mrp',
    name: 'Manufacturing MRP pack',
    expertise: 'MRP & shop floor',
    verticals: ['Manufacturing'],
    crossVertical: false,
    contains: ['Blueprint', 'MRP & routings config', 'Test sets', 'Training deck'],
    reuseCount: 6,
    daysSaved: 9,
    ownerId: 'camille-laurent',
    version: 'V3',
    odooVersion: 19,
    status: 'Published',
  },
  {
    id: 'food-service-ops',
    name: 'Food service operations pack',
    expertise: 'Inventory & traceability',
    verticals: ['Food service'],
    crossVertical: false,
    contains: ['Blueprint', 'Traceability config', 'Migration mappings', 'Test sets'],
    reuseCount: 5,
    daysSaved: 7,
    ownerId: 'femke-de-boer',
    version: 'V2',
    odooVersion: 19,
    status: 'Update in review',
  },
  {
    id: 'einvoicing-fr',
    name: 'E-invoicing pack · FR',
    expertise: 'Finance & compliance (Factur-X)',
    verticals: ['Retail', 'Food service'],
    crossVertical: true,
    contains: ['Factur-X config', 'Compliance checklist', 'Test sets'],
    reuseCount: 5,
    daysSaved: 4,
    ownerId: 'thomas-peeters',
    version: 'V3',
    odooVersion: 19,
    status: 'Published',
  },
  {
    id: 'healthcare-intake-be',
    name: 'Healthcare intake pack · BE',
    expertise: 'Intake & payroll (BE)',
    verticals: ['Healthcare'],
    crossVertical: false,
    contains: ['Blueprint', 'Payroll & social-secretariat config', 'Test sets'],
    reuseCount: 3,
    daysSaved: 8,
    ownerId: 'margot-claes',
    version: 'V2',
    odooVersion: 18,
    status: 'Published',
  },
  {
    id: 'reporting-pack',
    name: 'Reporting pack',
    expertise: 'Reporting & KPIs',
    verticals: ['Retail', 'Manufacturing', 'Healthcare'],
    crossVertical: true,
    contains: ['Dashboard set', 'KPI definitions', 'Test sets'],
    reuseCount: 3,
    daysSaved: 3,
    ownerId: 'lotte-willems',
    version: 'V2',
    odooVersion: 18,
    status: 'Published',
  },
] as const

export const heroPack = (): TemplatePack => {
  const pack = TEMPLATE_PACKS.find((p) => p.id === HERO_PACK_ID)
  if (!pack) throw new Error(`Unknown template pack: ${HERO_PACK_ID}`)
  return pack
}

export const packOwner = (pack: TemplatePack): Person => personById(pack.ownerId)

export const packUsedBy = (pack: TemplatePack): readonly Project[] => {
  const { usedByProjectIds = [] } = pack
  return usedByProjectIds.map(projectById)
}

export const isHeroPack = (pack: TemplatePack): boolean => pack.id === HERO_PACK_ID

/* ── labels ─────────────────────────────────────────────────────────────── */

export const CROSS_VERTICAL_LABEL = 'Any vertical'

/** "Retail × Pricing & promotions" / "Any vertical × Data migration". */
export const cellLabel = (pack: TemplatePack): string => {
  const [first] = pack.verticals
  const industry = pack.crossVertical ? CROSS_VERTICAL_LABEL : (first ?? CROSS_VERTICAL_LABEL)
  return `${industry} × ${pack.expertise}`
}

/** Why a vertical-agnostic pack still answers only some chips. */
export const provenInLabel = (pack: TemplatePack): string =>
  `proven in ${pack.verticals.join(' · ')}`

export const versionLabel = (pack: TemplatePack): string =>
  `${pack.version} · Odoo ${pack.odooVersion}`

export const reuseLabel = (pack: TemplatePack): string => `${pack.reuseCount}×`
export const daysSavedLabel = (pack: TemplatePack): string => `~${pack.daysSaved} days`
export const ownerMeta = (owner: Person): string => `${owner.office} · ${owner.role}`

/** Published packs stay quiet; only the one needing a human wears a pill. */
export const packStatusPill = (
  pack: TemplatePack
): { label: PackStatus; tone: StatusTone } | null =>
  pack.status === 'Published' ? null : { label: pack.status, tone: 'review' }

/* ── filtering ──────────────────────────────────────────────────────────── */

export const packsInVertical = (vertical: VerticalFilter): readonly TemplatePack[] =>
  vertical === ALL
    ? TEMPLATE_PACKS
    : TEMPLATE_PACKS.filter((pack) => pack.verticals.includes(vertical))

export const matchesFilters = (
  pack: TemplatePack,
  vertical: VerticalFilter,
  odoo: OdooFilter
): boolean => {
  if (vertical !== ALL && !pack.verticals.includes(vertical)) return false
  if (odoo !== ALL && pack.odooVersion !== odoo) return false
  return true
}

export interface VerticalChip {
  key: VerticalFilter
  label: string
  /** Packs proven in this vertical — absent on the "All" chip. */
  count?: number
}

export const VERTICAL_CHIPS: readonly VerticalChip[] = [
  { key: ALL, label: 'All' },
  ...VERTICALS.map((vertical) => ({
    key: vertical as VerticalFilter,
    label: vertical,
    count: packsInVertical(vertical).length,
  })),
]

/* ── routes (no surface builds an href by hand) ─────────────────────────── */

export const ROUTES = {
  templateDetail: (packId: string) => `/library/templates/${packId}`,
  person: (personId: string) => `/resources/people/${personId}`,
  projects: '/projects',
  heroBuildBoard: `/project/${HERO_PROJECT_ID}/build`,
} as const

/* ── panels (deck figures for this screen, transcribed as given) ────────── */

const CONTRIBUTION_ASSETS = 19
const RETIRED_ASSETS = 7
/** Median build effort against the pre-library baseline. U+2212, not a hyphen. */
const ROLLOUT_EFFECT = '−31%'

export const PANELS = {
  contribution: {
    label: 'Contribution this quarter',
    figure: String(CONTRIBUTION_ASSETS),
    unit: 'assets',
    qualifier: 'harvested automatically at project close; the consultant confirms, PX packages',
  },
  retired: {
    label: 'Retired',
    figure: String(RETIRED_ASSETS),
    unit: 'assets',
    qualifier: 'superseded or version-lapsed',
  },
  effect: {
    label: 'Estimated effect on a rollout',
    figure: ROLLOUT_EFFECT,
    qualifier: {
      before: 'median ',
      link: { label: 'build effort', to: ROUTES.heroBuildBoard },
      after: ' vs. pre-library baseline',
    },
  },
} as const

/* ── copy ───────────────────────────────────────────────────────────────── */

const phaseName = (project: Project): string => {
  const phase = PHASES.find((p) => p.key === project.phase)
  if (!phase) throw new Error(`Unknown phase: ${project.phase}`)
  return phase.name
}

/**
 * The honest reason a slice is empty, in the product's own voice: either the
 * group has never closed a project in that vertical, or the packs it does have
 * sit on the other Odoo version.
 */
export const emptyBody = (vertical: VerticalFilter, odoo: OdooFilter): string => {
  if (vertical === ALL) {
    return `No pack is published for ${odooOptionLabel(odoo)} — clear the version filter to see all ${TEMPLATE_PACKS.length}.`
  }

  const inVertical = packsInVertical(vertical)
  if (inVertical.length > 0) {
    const others = inVertical.length
    return `No ${vertical} pack is published for ${odooOptionLabel(odoo)}; ${others === 1 ? 'the one that exists sits' : `the ${others} that exist sit`} on the other version.`
  }

  const project = PROJECTS.find((p) => p.vertical === vertical)
  return project
    ? `The library has no ${vertical} pack yet — a project pays into the library at close, and ${project.client} is still in ${phaseName(project)}.`
    : `The library has no ${vertical} pack yet — the group has no ${vertical} engagement to harvest one from.`
}

export const COPY = {
  filters: {
    verticalLegend: 'Vertical',
    versionLabel: 'Odoo version',
    resultCount: (shown: number, total: number) =>
      `${shown} of ${total} pack${total === 1 ? '' : 's'}`,
  },
  columns: {
    pack: 'Template pack',
    cell: 'Vertical × expertise',
    contains: 'Contains',
    reuse: 'Reuse',
    timeSaved: 'Time saved',
    owner: 'Owner',
  },
  tableCaption: `The ${TEMPLATE_PACKS.length} template packs of the library, with the industry × expertise cell each covers, what it contains, how often it has been reused, the days it saves a rollout and who maintains it.`,
  usedByPrefix: 'Used by',
  usedBySeparator: '·',
  nonHeroNote: `Detail follows the hero pack this round — only ${heroPack().name} opens; the other ${TEMPLATE_PACKS.length - 1} names are text rather than links that would go nowhere.`,
  empty: {
    title: 'Nothing in this cell',
    action: 'Clear filters',
  },
  densityNote: {
    before: `The table shows the ${TEMPLATE_PACKS.length} packs of the library's ${GROUP_STATS.library.assets} assets — the individual blueprints, configs, mappings and test sets live inside a pack, and `,
    link: { label: 'the retail pack opens its contents', to: ROUTES.templateDetail(HERO_PACK_ID) },
    after: `. Every project either spends from the library or pays into it, and the reuse score decides which packs get invested in: ${PORTFOLIO.reuseRatePct}% of delivered scope is already met from the library.`,
  },
} as const
